import axios, { AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { useAuthStore } from '../stores/auth.store';
import { fingerprintToHeaders, type BrowserFingerprint } from '../auth/fingerprint';
import { env } from '../env';

// Use the API URL directly
const API_URL = env.VITE_API_URL;

let isRefreshing = false;
let isRedirecting = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

export const setFingerprint = (fingerprint: BrowserFingerprint | null) => {
  if (fingerprint) {
    useAuthStore.getState().setFingerprint(fingerprint);
  }
};

export const getAccessToken = () => useAuthStore.getState().accessToken;

export const clearTokens = () => {
  useAuthStore.getState().logout();
};

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const { accessToken, fingerprint } = useAuthStore.getState();

    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    if (fingerprint && config.headers) {
      const fpHeaders = fingerprintToHeaders(fingerprint);
      Object.entries(fpHeaders).forEach(([key, value]) => {
        config.headers[key] = value;
      });

      if (env.isDev) {
        console.log(`[API] Token + fingerprint attached for ${config.method?.toUpperCase()} ${config.url}`);
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Custom error class for rate limiting / account lockout
export class RateLimitError extends Error {
  retryAfter?: number;
  constructor(message: string, retryAfter?: number) {
    super(message);
    this.name = 'RateLimitError';
    this.retryAfter = retryAfter;
  }
}

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 429 Too Many Requests (Rate Limiting / Account Lockout)
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after'];
      const retrySeconds = retryAfter ? parseInt(retryAfter, 10) : undefined;
      const errorMessage = (error.response.data as { message?: string })?.message
        || 'Too many requests. Please try again later.';
      return Promise.reject(new RateLimitError(errorMessage, retrySeconds));
    }

    if (originalRequest?.url?.includes('/auth/')) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { refreshToken, fingerprint } = useAuthStore.getState();

        if (refreshToken) {
          try {
            const refreshHeaders: Record<string, string> = {
              'Content-Type': 'application/json',
            };
            if (fingerprint) {
              Object.assign(refreshHeaders, fingerprintToHeaders(fingerprint));
            }

            const refreshResponse = await axios.post(`${API_URL}/auth/refresh`, {
              refresh_token: refreshToken,
            }, { headers: refreshHeaders });

            const newAccessToken = refreshResponse.data?.data?.access_token;
            const newRefreshToken = refreshResponse.data?.data?.refresh_token;

            if (newAccessToken) {
              useAuthStore.getState().updateTokens({
                accessToken: newAccessToken,
                refreshToken: newRefreshToken,
              });

              processQueue(null, newAccessToken);

              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
              }
              return apiClient(originalRequest);
            }
          } catch {
            // Refresh failed, continue to logout
          }
        }

        processQueue(new Error('Session expired'), null);
        await handleLogout();
        return Promise.reject(error);
      } catch (refreshError) {
        processQueue(refreshError as Error, null);
        await handleLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

async function handleLogout() {
  if (isRedirecting) return;

  isRedirecting = true;
  clearTokens();

  if (!window.location.pathname.includes('/login')) {
    window.location.href = '/login';
  }

  setTimeout(() => {
    isRedirecting = false;
  }, 3000);
}

export default apiClient;
